"use client";

import * as TooltipPrimitive from "@radix-ui/react-tooltip";
import * as React from "react";

import { cn } from "../lib/utils";
import { Button } from "./button";
import { IconComponent } from "./icon";

const TooltipProvider = TooltipPrimitive.Provider;

const TooltipTrigger = TooltipPrimitive.Trigger;

const TooltipContent = React.forwardRef<
  React.ElementRef<typeof TooltipPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof TooltipPrimitive.Content>
>(({ className, sideOffset = 4, ...props }, ref) => (
  <TooltipPrimitive.Content
    className={cn(
      "z-[1000] w-fit max-w-[90dvw] origin-[--radix-tooltip-content-transform-origin] overflow-hidden rounded-md bg-primary px-3 py-1.5 text-xs text-primary-foreground animate-in fade-in-0 zoom-in-95 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 sm:max-w-[340px]",

      className,
    )}
    ref={ref}
    sideOffset={sideOffset}
    {...props}
  />
));
TooltipContent.displayName = TooltipPrimitive.Content.displayName;

interface TooltipProps
  extends
    React.ComponentProps<typeof TooltipPrimitive.Root>,
    React.ComponentPropsWithoutRef<typeof TooltipPrimitive.Content> {
  content?: string;
  icon?: IconComponent;
}

/**
 * Radix UI (API reference): https://www.radix-ui.com/primitives/docs/components/tooltip#api-reference
 *
 * shadcn/ui docs: https://ui.shadcn.com/docs/components/tooltip
 */
const Tooltip = React.forwardRef<
  React.ElementRef<typeof TooltipPrimitive.Content>,
  TooltipProps
>(
  (
    {
      open,
      defaultOpen,
      delayDuration,
      disableHoverableContent,
      onOpenChange,
      avoidCollisions = true,
      ...props
    },
    ref,
  ) => {
    const showContent = props?.content && props?.content.length > 0;
    const content = props?.icon ? (
      <Button icon={props.icon} size="content" variant="transparent" />
    ) : (
      props?.content
    );

    return (
      <TooltipPrimitive.Root
        defaultOpen={defaultOpen}
        delayDuration={delayDuration}
        disableHoverableContent={disableHoverableContent}
        onOpenChange={onOpenChange}
        open={open}
      >
        <TooltipPrimitive.Trigger asChild>
          {props.children}
        </TooltipPrimitive.Trigger>

        {showContent && (
          <TooltipContent
            ref={ref}
            {...props}
            avoidCollisions={avoidCollisions}
          >
            {content}
          </TooltipContent>
        )}
      </TooltipPrimitive.Root>
    );
  },
);
Tooltip.displayName = "Tooltip";

type TooltipProviderProps = React.ComponentProps<
  typeof TooltipPrimitive.Provider
>;

export {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
  type TooltipProps,
  type TooltipProviderProps,
};
